import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, User } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import TeamCrest from "@/components/TeamCrest";
import PhotoUpload from "@/components/PhotoUpload";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const POSITIONS = ["PG", "SG", "SF", "PF", "C"];
const EMPTY = { team_id: "", name: "", number: "", position: "PG", photo_url: "", ppg: "", apg: "", rpg: "", spg: "", blk: "" };

export default function RosterTab() {
    const [teams, setTeams] = useState([]);
    const [selectedTeam, setSelectedTeam] = useState("");
    const [players, setPlayers] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/teams").then((r) => {
            setTeams(r.data);
            if (r.data.length && !selectedTeam) setSelectedTeam(r.data[0].id);
        }).catch(() => {});
    }, []);

    const loadPlayers = (tid) => {
        if (!tid) return;
        api.get(`/teams/${tid}`).then((r) => setPlayers(r.data.players)).catch(() => {});
    };
    useEffect(() => {
        loadPlayers(selectedTeam);
    }, [selectedTeam]);

    const openCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY, team_id: selectedTeam });
        setOpen(true);
    };
    const openEdit = (p) => {
        setEditing(p);
        setForm({ team_id: p.team_id, name: p.name, number: String(p.number), position: p.position, photo_url: p.photo_url || "",
            ppg: p.ppg ? String(p.ppg) : "", apg: p.apg ? String(p.apg) : "", rpg: p.rpg ? String(p.rpg) : "",
            spg: p.spg ? String(p.spg) : "", blk: p.blk ? String(p.blk) : "" });
        setOpen(true);
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = {
            ...form,
            number: parseInt(form.number, 10),
            ppg: parseFloat(form.ppg) || 0, apg: parseFloat(form.apg) || 0, rpg: parseFloat(form.rpg) || 0,
            spg: parseFloat(form.spg) || 0, blk: parseFloat(form.blk) || 0,
        };
        try {
            if (editing) await api.put(`/players/${editing.id}`, payload);
            else await api.post("/players", payload);
            toast.success(editing ? "Player updated" : "Player added");
            setOpen(false);
            loadPlayers(selectedTeam);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Remove ${p.name} from the roster?`)) return;
        try {
            await api.delete(`/players/${p.id}`);
            toast.success("Player removed");
            loadPlayers(selectedTeam);
        } catch (err) {
            toast.error(apiError(err));
        }
    };

    const team = teams.find((t) => t.id === selectedTeam);

    return (
        <div data-testid="admin-roster-tab">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                    <h2 className="font-display font-bold uppercase text-xl text-white">Roster</h2>
                    <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                        <SelectTrigger data-testid="roster-team-select" className="w-56 bg-[#08080C] border-[#262638] text-white">
                            <SelectValue placeholder="Select team" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                            {teams.map((t) => (
                                <SelectItem key={t.id} value={t.id} data-testid={`roster-team-option-${t.id}`}>
                                    {t.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <button
                    data-testid="add-player-button"
                    onClick={openCreate}
                    disabled={!selectedTeam}
                    className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-50 text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                >
                    <Plus className="w-4 h-4" /> Add Player
                </button>
            </div>

            {players.length === 0 ? (
                <div data-testid="admin-roster-empty" className="card-arena rounded-2xl p-12 text-center">
                    <p className="font-display font-bold uppercase text-lg text-slate-300">No players yet</p>
                    <p className="text-sm text-slate-500 mt-2">Add your first signing to {team?.name}.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {players.map((p) => (
                        <div key={p.id} data-testid={`admin-player-card-${p.id}`} className="card-arena rounded-2xl p-4 flex items-center gap-4">
                            {p.photo_url ? (
                                <img src={p.photo_url} alt={p.name} className="w-14 h-14 rounded-xl object-cover object-top" />
                            ) : (
                                <div className="w-14 h-14 rounded-xl bg-[#1A1A28] flex items-center justify-center">
                                    <User className="w-6 h-6 text-slate-600" />
                                </div>
                            )}
                            <div className="flex-1 min-w-0">
                                <p className="font-display font-bold uppercase text-white truncate">
                                    <span className="font-stat text-[#FF5500] mr-2">#{p.number}</span>
                                    {p.name}
                                </p>
                                <p className="text-xs text-slate-500">{p.position} • {team?.name}</p>
                            </div>
                            <div className="flex gap-1">
                                <button
                                    data-testid={`edit-player-${p.id}`}
                                    onClick={() => openEdit(p)}
                                    className="p-2 text-slate-400 hover:text-[#FFB800] transition-colors duration-200"
                                    aria-label={`Edit ${p.name}`}
                                >
                                    <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                    data-testid={`delete-player-${p.id}`}
                                    onClick={() => remove(p)}
                                    className="p-2 text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                                    aria-label={`Delete ${p.name}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-display font-black uppercase text-2xl">
                            {editing ? "Edit Player" : "New Signing"}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Add or edit a roster player with number, position and photo.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} data-testid="player-form" className="space-y-4 mt-2">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Team</Label>
                            <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                                <SelectTrigger data-testid="player-form-team" className="mt-2 bg-[#08080C] border-[#262638] text-white">
                                    <SelectValue placeholder="Select team" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                    {teams.map((t) => (
                                        <SelectItem key={t.id} value={t.id}>
                                            {t.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Player Name</Label>
                            <Input
                                required
                                data-testid="player-form-name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                                placeholder="Kai Mercado"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Jersey #</Label>
                                <Input
                                    required
                                    type="number"
                                    min="0"
                                    max="99"
                                    data-testid="player-form-number"
                                    value={form.number}
                                    onChange={(e) => setForm({ ...form, number: e.target.value })}
                                    className="mt-2 bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Position</Label>
                                <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
                                    <SelectTrigger data-testid="player-form-position" className="mt-2 bg-[#08080C] border-[#262638] text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                        {POSITIONS.map((p) => (
                                            <SelectItem key={p} value={p}>
                                                {p}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">
                                Season Averages
                            </Label>
                            <div className="grid grid-cols-5 gap-2 mt-2">
                                {[
                                    ["ppg", "PTS"],
                                    ["apg", "AST"],
                                    ["rpg", "REB"],
                                    ["spg", "STL"],
                                    ["blk", "BLK"],
                                ].map(([key, label]) => (
                                    <div key={key}>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            data-testid={`player-form-${key}`}
                                            value={form[key]}
                                            onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                            className="bg-[#08080C] border-[#262638] focus:border-[#FF5500] text-white font-stat text-center px-1"
                                        />
                                        <p className="text-[9px] uppercase tracking-widest text-slate-500 text-center mt-1">
                                            {label}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <PhotoUpload
                            testid="player-form-photo"
                            label="Player Photo"
                            value={form.photo_url}
                            onChange={(url) => setForm({ ...form, photo_url: url })}
                        />
                        <button
                            type="submit"
                            data-testid="player-form-submit"
                            disabled={saving || !form.team_id}
                            className="w-full flex items-center justify-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-60 text-white font-display font-bold uppercase tracking-widest py-3 rounded-full transition-colors duration-200"
                        >
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                            {editing ? "Save Changes" : "Sign Player"}
                        </button>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
